import type { ContactEntry } from "./helpers/client";
import { toCalendarDate, daysSince, parseCalendarDate } from "./helpers/client";

export interface ContactLogDay<T> {
    /** `YYYY-MM-DD`, in the reader's time zone. */
    day: string;
    entries: T[];
}

/**
 * The log's entries as runs of one calendar day each, in the order given — newest first, as the API
 * returns them. The day is the reader's own: a call at 11 PM on the West Coast is that evening's,
 * not the next morning's in UTC.
 */
export const groupByDay = <T extends { contact: Pick<ContactEntry, "contactedAt"> }>(
    entries: readonly T[],
): ContactLogDay<T>[] => {
    const days: ContactLogDay<T>[] = [];
    for (const entry of entries) {
        const day = toCalendarDate(new Date(entry.contact.contactedAt));
        const current = days[days.length - 1];
        if (current?.day === day) current.entries.push(entry);
        else days.push({ day, entries: [entry] });
    }
    return days;
};

/** "today" or "yesterday" for those two days, which the heading names before the date. */
export const relativeDay = (day: string, today: Date): "today" | "yesterday" | undefined => {
    const ago = daysSince(day, today);
    return ago === 0 ? "today" : ago === 1 ? "yesterday" : undefined;
};

const DAY_FORMAT = new Intl.DateTimeFormat("en-US", { weekday: "short", month: "short", day: "numeric" });
const DAY_WITH_YEAR_FORMAT = new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
});

/** "Sun, Jul 26" — with the year only once it is not this one's, where a date alone would mislead. */
export const formatLogDay = (day: string, today: Date): string => {
    const date = parseCalendarDate(day);
    return (date.getFullYear() === today.getFullYear() ? DAY_FORMAT : DAY_WITH_YEAR_FORMAT).format(date);
};
